import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BroadcastService,
  ClientConstants,
  ClientRulesService,
  DataService,
  DocumentUpdateService,
  IndexedDBService,
  LocalStorageKeys,
  LocalStorageService,
  LoggedInUserService,
  NotificationService,
  OfflineStatusService,
  OfflineUtilService,
  PageVisibilityService,
  SessionTestService,
  StorageEventService,
  UnsavedDocumentService,
  WebSocketService,
  select
} from '@formbird/services';
import { SharedConstants, User } from '@formbird/types';
import { ListenServiceWorkerService } from '@services/listen-service-worker';
import { ModalService } from '@services/modal/modal.service';
import { Observable } from 'rxjs';
import { AppVersion } from '../AppVersion';
import { AuthenticationService } from './authentication/authentication.service';
import { CacheLoadService } from './cache-load.service';
import { FormbirdInjectorService } from './injector/formbird-injector.service';
import { KeyboardShortcutService } from './keyboard-shortcut.service';
import { StateChangeService } from './state-change/state-change.service';
import { CacheMapTilesService } from './tileserver-gl/cache-map-tiles.service';
const logger = console;

@Injectable({
  providedIn: 'root'
})
export class InitialisationService {
  private clientRulesService = inject(ClientRulesService);
  private webSocketService = inject(WebSocketService);
  private storageEventService = inject(StorageEventService);
  private notificationService = inject(NotificationService);
  private keyboardShortcutService = inject(KeyboardShortcutService);
  private modalService = inject(ModalService);
  private dataService = inject(DataService);
  private offlineStatusService = inject(OfflineStatusService);
  private stateChangeService = inject(StateChangeService);
  private loggedInUserService = inject(LoggedInUserService);
  private localStorageService = inject(LocalStorageService);
  private cacheLoadService = inject(CacheLoadService);
  private sessionTestService = inject(SessionTestService);
  private formbirdInjectorService = inject(FormbirdInjectorService);
  private pageVisibilityService = inject(PageVisibilityService);
  private authenticationService = inject(AuthenticationService);
  private listenServiceWorkerService = inject(ListenServiceWorkerService);
  private offlineUtilService = inject(OfflineUtilService);
  private cacheMapTilesService = inject(CacheMapTilesService);
  private documentUpdateService = inject(DocumentUpdateService);
  private indexedDBService = inject(IndexedDBService);
  private unsavedDocumentService = inject(UnsavedDocumentService);
  private broadcastService = inject(BroadcastService);
  private route = inject(ActivatedRoute);

  @select(['userState', 'user']) user$: Observable<User>;
  user: any;

  constructor() {
    this.user$.subscribe((user: User) => this.user = user);
  }

  initialise() {

    this.listenServiceWorkerService.listenServiceWorkerMessage();
    const userId = this.localStorageService.getItem(LocalStorageKeys.LOGGED_IN_USER_ID);
    if (userId && !this.offlineStatusService.shouldOperateOnline()) {
      return;
    } else {
      this.sessionTestService
        .testSession()
        .then(
          res => {
            //mantis #17831 - if logout when online/offline when caching enabled, we need to display login form
            // instead of form is displayed when Network connection is lost and because the network request can't be made.
            if (res === "" && !this.localStorageService.getItem(LocalStorageKeys.LOGGED_IN_USER_ID)) {
              this.authenticationService.showLogin();
              logger.error('Unauthorized');
            } else {
              // set status to connected as soon as the connection has been successfully shown to be online so that the online
              // indicator will not start as red and then change to green
              this.offlineStatusService.setConnected();
            }
          },
          err => {
            // an error of 401 is expected if the user is not logged in
            if (err.status === 401) {
              // this indicates the user needs to be logged in, so they will be taken to the login screen on
              // initialisation
              this.localStorageService.removeItem(LocalStorageKeys.LOGGED_IN_USER_ID);
              logger.error('Unauthorized');
            } else {
              logger.error(err);
            }
          }
        );
      return
    }

  }

  private setupDefaultServices() {
    // start the websocket service so that it can connect on regaining the connection if offline
    this.storageEventService.start();
    // this.notificationService.setupAlert(this.$rootScope, this.configService.clientConfig().maxAlertMessages);
    this.keyboardShortcutService.initialise(); // gets unbound on scope destroy
    this.pageVisibilityService.startListeningForPageVisibility();
  }

  terminateDefaultServices() {
    this.webSocketService.end();
    this.storageEventService.end();
    this.notificationService.reverseSetupAlert();
    // note that InitialisationCtrl initialised DataService
    this.dataService.reset();
    this.pageVisibilityService.stopListeningForPageVisibility();
  }

  async postLoginInitialise() {
    this.offlineStatusService.setConnected();

    try {
      await this.loggedInUserService.setLoggedUser();
    } catch (err) {
      if (err.status === 401) {
        const message = 'Error on post login initialization : ' + err.message;
        logger.error(message);
        await this.loggedInUserService.logoutUser();
        window.location.reload();
      }
    }

    try {

      const dataSub = this.route.data.subscribe(async data => {
        if (this.loggedInUserService.getLoggedInUser()) {
          this.localStorageService.setItem(LocalStorageKeys.TOP_BAR_INITIALIZED, 'true');
        } else {
          this.loggedInUserService.loadLoggedUser().then(user => {
            this.localStorageService.setItem(LocalStorageKeys.TOP_BAR_INITIALIZED, 'true');
          }).catch(error => {
            this.localStorageService.setItem(LocalStorageKeys.TOP_BAR_INITIALIZED, 'false');
          });
        }
      });
      await this.dataService.initialise();
      await this.stateChangeService.handlePostLoginSuccess();

      this.loadDataCache();

      if (this.offlineStatusService.shouldOperateOnline()) {
        this.webSocketService.start();
      }

      if (this.loggedInUserService.getUserConfigItem('toEnableCache') === true) {
        this.offlineInitialise();
        this.enableCaching();
        // caching now enabled
        this.loggedInUserService.setUserConfigItem('toEnableCache', false);
      } else if (this.offlineStatusService.isCachingEnabled()) {
        //let offlineInitialise start the services and call handlePostLoginSuccess
        this.offlineInitialise();
        this.offlineStatusService.offlineStatus.errorCacheAttachedFile =
          this.loggedInUserService.getUserConfigItem('ErrorCachingAttachedFiles') ?
            this.loggedInUserService.getUserConfigItem('ErrorCachingAttachedFiles') : [];

        this.offlineStatusService.offlineStatus.errorOfflineChangesSyncDocs =
          this.loggedInUserService.getUserConfigItem('ErrorOfflineChangesSyncDocs') ?
            this.loggedInUserService.getUserConfigItem('ErrorOfflineChangesSyncDocs') : [];

        const pendingCount = this.loggedInUserService.getUserConfigItem('offlinePendingDocumentsCount') ?
          this.loggedInUserService.getUserConfigItem('offlinePendingDocumentsCount') : 0;
        this.offlineStatusService.setPendingDocumentCount(pendingCount);
        this.offlineStatusService.setCachingEnabledStatus(true);

        this.offlineUtilService.startOfflinePoller();

      } else {
        this.setupDefaultServices();
      }

      const lastEnteredUrl = this.localStorageService.getItem(ClientConstants.LAST_ENTERED_STATE_URL);
      if (lastEnteredUrl) {
        logger.info("Last entered state: " + lastEnteredUrl);
        this.localStorageService.removeItem(ClientConstants.LAST_ENTERED_STATE_URL);
        window.location.href = lastEnteredUrl;
      }

      this.webSocketService.addEventListener(SharedConstants.WEB_SOCKET_MESSAGE_SIGN_OUT_ALL_DEVICE, (userId) => {
        if (userId === this.user?.account?.documentId) {
          this.notificationService.info('You have been signed out of all devices.');
          this.authenticationService.showLogin();
        }
      });

    } catch (err) {
      const connected = await this.offlineStatusService.checkServerConnectionStatus();
      const cachingEnabled = this.offlineStatusService.isCachingEnabled();
      let message = 'Error on post login initialization : ' + err.message;
      if (!cachingEnabled && !connected) {
        message = 'Sorry, the network connection has been lost and caching has not been enabled.';
      }

      logger.error(message);
      this.notificationService.error(message);
      const reload = () => window.location.reload();
      this.modalService.showDialog(message + ' <br/> Reload the page?', 'Warning', reload, null, null, reload);
    }
  }

  async loadDataCache() {
    try {
      const connected = await this.offlineStatusService.checkServerConnectionStatus();
      if (connected) {
        this.dataService.loadDataCache(false);
        if (!this.loggedInUserService.getUserConfigItem('initialCacheCompleted') && this.offlineStatusService.isCachingEnabled()) {
          this.cacheMapTilesService.loadDataCache();
          this.cacheLoadService.cacheStaticAssets();
        }
      }
    } catch (err) {
      logger.error('Error loading data cache: ' + err);
    }
  }

  async offlineInitialise() {//Use this all the time to enable caching.
    try {
      const _self = this;
      await this.cacheLoadService.restoreOfflineUserCredentials();

      _self.storageEventService.addListener((event) => {
        if (event.key === SharedConstants.WEB_SOCKET_MESSAGE_SAVE_DOCUMENT) {
          const newValue = JSON.parse(event.newValue);
          if (newValue && newValue.versionId && newValue.documentId) {
            if (_self.unsavedDocumentService.documentIsLoaded(newValue.documentId)) {
              _self.indexedDBService.loadDocByVersion(newValue.versionId, async (err, version) => {
                await _self.documentUpdateService.updateVersions([version]);
                if (version && version.systemHeader.deleted) {
                  _self.broadcastService.broadcast(ClientConstants.DOCUMENT_DELETED, version.documentId);
                } else {
                  _self.broadcastService.broadcast(ClientConstants.DOCUMENT_UPDATED, [version]);
                }

              });
            }
          }
        }
      });

      logger.info('User credentials successfully restored. Setting up default services...');
      this.setupDefaultServices();
      // the app needs to behave as though logged in. If the user's session is no longer valid
      // when they come online they will need to login again to be able to sync the documents
      // saved offline and continue to use the app
      await this.stateChangeService.handlePostLoginSuccess();
    } catch (err) {
      logger.error(err);
    }
  }

  async enableCaching() {
    if (typeof SharedWorker !== 'undefined' || typeof Worker !== "undefined") {
      // this is to load the logged user again after the caching is enabled, 
      // so that api/loggedUserData is called and cached in service worker
      await this.loggedInUserService.loadLoggedUser();
      const controlDoc = this.loggedInUserService.getUserControlDocument();
      const hasOfflineAccess = controlDoc.accessKeys
        .some(key => key.rights.includes(SharedConstants.OPERATION_TYPE_OFFLINE));

      if (hasOfflineAccess) {
        try {
          await this.cacheLoadService.enableCaching();
          const msg = 'Offline data loaded.';
          logger.info(msg);
          return msg;
        } catch (err) {
          logger.error('Error loading offline data: ' + err);
          logger.error(err);
          throw err;
        }
      } else {
        throw new Error('You do not have any documents configured for offline. Caching cannot be enabled.');
      }
    } else {
      throw new Error('Cannot enable caching. Please upgrade your browser.');
    }
  }

  /**
   * set up service to be called globally from components and other code external to the Angular app
   */
  setupServices() {
    this.formbirdInjectorService.setupServices();
    this.clientRulesService.setModalService(this.modalService);
  }

  async setupAppInfo() {
    const appVersion = AppVersion.FIELDTEC_APP_VERSION;
    this.localStorageService.setItem(LocalStorageKeys.APP_VERSION, appVersion);
  }

}
